import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, Calendar as Cal, Clock, CheckCircle, XCircle } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import api from "@/lib/api";

export default function Manage() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState(null); // null | reschedule | done | cancelled
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [refundInfo, setRefundInfo] = useState(null);
  const [closedWeekdays, setClosedWeekdays] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);

  useEffect(() => {
    api.get("/availability/config").then((r) => {
      setClosedWeekdays(r.data.closed_weekdays || []);
      setBlockedDates(r.data.blocked_dates || []);
    }).catch(() => {});
  }, []);

  const disabledDays = useMemo(() => {
    const m = [{ before: new Date(new Date().setHours(0, 0, 0, 0)) }];
    if (closedWeekdays.length) m.push({ dayOfWeek: closedWeekdays });
    blockedDates.forEach((s) => { const [y, mm, dd] = s.split("-").map(Number); m.push(new Date(y, mm - 1, dd)); });
    return m;
  }, [closedWeekdays, blockedDates]);

  const load = () =>
    api.get(`/bookings/manage/${token}`).then((r) => setBooking(r.data)).catch(() => setNotFound(true));

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSlots = async (d) => {
    setLoadingSlots(true); setSlots(null); setTime(null);
    try {
      const { data } = await api.get("/availability", { params: { date: format(d, "yyyy-MM-dd"), service_id: booking.service_id } });
      setSlots(data.slots);
    } catch { setSlots([]); } finally { setLoadingSlots(false); }
  };

  const doReschedule = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/bookings/manage/${token}/reschedule`, { date: format(date, "yyyy-MM-dd"), time });
      setBooking({ ...booking, date_display: data.date_display, time: data.time });
      setMode("done");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not reschedule."); setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/bookings/manage/${token}/cancel`);
      setRefundInfo(data.refund);
      setMode("cancelled");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not cancel."); setBusy(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#FAF8F5] text-[#1C1917] flex items-center justify-center px-5 py-16">
      <div className="max-w-lg w-full">{children}</div>
    </div>
  );

  if (notFound)
    return (
      <Shell>
        <div data-testid="manage-notfound" className="text-center border border-[#1C1917]/10 bg-white p-10">
          <h1 className="font-serif text-3xl">Booking not found</h1>
          <p className="text-[#1C1917]/50 mt-3 text-sm">This link may have expired.</p>
          <Link to="/" className="inline-block mt-6 border border-[#1C1917]/15 hover:border-[#C5B382] text-[11px] tracking-[0.25em] uppercase px-8 py-3.5 transition-colors">Back to Home</Link>
        </div>
      </Shell>
    );

  if (!booking) return <Shell><div className="text-center"><Loader2 className="animate-spin text-[#C5B382] mx-auto" /></div></Shell>;

  const isCancelled = booking.status === "cancelled" || mode === "cancelled";

  return (
    <Shell>
      <div data-testid="manage-page" className="border border-[#1C1917]/10 bg-white">
        <div className="p-8 border-b border-[#1C1917]/10">
          <span className="text-[#C5B382] text-[11px] tracking-[0.35em] uppercase">Manage Booking</span>
          <h1 className="font-serif text-3xl mt-3">{booking.service_name}</h1>
          <div className="mt-4 space-y-2 text-sm text-[#1C1917]/70">
            <div className="flex items-center gap-2"><Cal size={15} className="text-[#C5B382]" /> {booking.date_display}</div>
            <div className="flex items-center gap-2"><Clock size={15} className="text-[#C5B382]" /> {booking.time}</div>
          </div>
        </div>

        {isCancelled && (
          <div data-testid="manage-cancelled" className="p-8 text-center">
            <XCircle size={36} className="text-[#1C1917]/40 mx-auto" />
            <p className="mt-4 text-[#1C1917]/70">This appointment has been cancelled. A confirmation email is on its way.</p>
            {refundInfo && (
              <p data-testid="manage-refund" className="mt-2 text-sm text-[#C5B382]">A refund of ${refundInfo.amount.toFixed(2)} has been issued to your card.</p>
            )}
            <Link to="/" className="inline-block mt-6 bg-[#C5B382] text-[#080808] text-[11px] font-semibold tracking-[0.25em] uppercase px-8 py-3.5">Book Again</Link>
          </div>
        )}

        {!isCancelled && mode === "done" && (
          <div data-testid="manage-done" className="p-8 text-center">
            <CheckCircle size={36} className="text-[#C5B382] mx-auto" />
            <p className="mt-4 text-[#1C1917]/80">Rescheduled to <strong>{booking.date_display}</strong> at <strong>{booking.time}</strong>. A confirmation email is on its way.</p>
            <Link to="/" className="inline-block mt-6 border border-[#1C1917]/15 hover:border-[#C5B382] text-[11px] tracking-[0.25em] uppercase px-8 py-3.5 transition-colors">Back to Home</Link>
          </div>
        )}

        {!isCancelled && mode === null && (
          <div className="p-8 flex flex-col sm:flex-row gap-3">
            <button data-testid="manage-reschedule-start" onClick={() => setMode("reschedule")} className="flex-1 bg-[#C5B382] hover:bg-white text-[#080808] text-[11px] font-semibold tracking-[0.25em] uppercase py-4 transition-colors">Reschedule</button>
            <button data-testid="manage-cancel" disabled={busy} onClick={() => (booking.is_late_cancel ? setMode("confirm-cancel") : doCancel())} className="flex-1 border border-[#1C1917]/15 hover:border-red-400 hover:text-red-400 text-[11px] font-semibold tracking-[0.25em] uppercase py-4 transition-colors">
              {busy ? "…" : "Cancel Appointment"}
            </button>
          </div>
        )}

        {!isCancelled && mode === "confirm-cancel" && (
          <div data-testid="manage-confirm-cancel" className="p-8 space-y-5">
            <div className="border border-red-300 bg-red-50 p-5">
              <p className="text-sm font-medium text-[#1C1917]">Less than 48 hours notice</p>
              <p className="text-sm text-[#1C1917]/65 mt-2 leading-relaxed">
                Cancellations made with less than forty-eight hours notice result in the loss of your retainer (your 50% deposit).
                {booking.payment_option === "full" && " As you paid in full, the balance above your retainer will be refunded to your card."}
              </p>
            </div>
            {error && <p data-testid="manage-error" className="text-red-500 text-sm">{error}</p>}
            <div className="flex items-center gap-3">
              <button onClick={() => setMode(null)} className="text-[#1C1917]/50 hover:text-[#1C1917] text-sm">Keep appointment</button>
              <button data-testid="manage-confirm-cancel-btn" disabled={busy} onClick={doCancel}
                className="ml-auto border border-red-400 text-red-500 hover:bg-red-500 hover:text-white text-[11px] font-semibold tracking-[0.25em] uppercase px-8 py-3.5 transition-colors">
                {busy ? "…" : "Cancel Anyway"}
              </button>
            </div>
          </div>
        )}

        {!isCancelled && mode === "reschedule" && (
          <div data-testid="manage-reschedule" className="p-8 space-y-5">
            <div className="flex justify-center bg-white border border-[#1C1917]/10 p-2">
              <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); fetchSlots(d); } }} disabled={disabledDays} className="text-[#1C1917]" />
            </div>
            {date && (
              loadingSlots ? <div className="flex justify-center py-4"><Loader2 className="animate-spin text-[#C5B382]" /></div>
              : slots && slots.length ? (
                <div className="grid grid-cols-4 gap-2">
                  {slots.map((s) => (
                    <button key={s.time} data-testid={`manage-slot-${s.time}`} disabled={!s.available} onClick={() => setTime(s.time)}
                      className={`py-2.5 text-sm border transition-all ${time === s.time ? "border-[#C5B382] bg-[#C5B382] text-[#080808] font-semibold" : s.available ? "border-[#1C1917]/15 hover:border-[#C5B382]" : "border-[#1C1917]/8 text-[#1C1917]/30 line-through cursor-not-allowed"}`}>
                      {s.time}
                    </button>
                  ))}
                </div>
              ) : <p className="text-[#1C1917]/50 text-sm py-2">No availability on this day.</p>
            )}
            {error && <p data-testid="manage-error" className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setMode(null)} className="text-[#1C1917]/50 hover:text-[#1C1917] text-sm">Back</button>
              <button data-testid="manage-reschedule-confirm" disabled={!date || !time || busy} onClick={doReschedule}
                className="ml-auto bg-[#C5B382] hover:bg-white disabled:opacity-30 text-[#080808] text-[11px] font-semibold tracking-[0.25em] uppercase px-8 py-3.5 transition-colors">
                {busy ? "…" : "Confirm New Time"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
